import { MailboxHealthCard } from "../MailboxAPI";
import { compareMailboxes } from "../mailboxHealth";
import MailboxRow from "./MailboxRow";

interface iMailboxListProps {
    mailboxes: MailboxHealthCard[];
}

/**
 * The flow column's label, used in both direction groups.
 *
 * Reads `Flow 24h`, not `In 24h` — the group heading above it already says
 * which direction. `TenantTable` splits the same two facts the other way
 * round — grouping by flow and labelling by direction — because there the
 * grouping axis IS flow. Here it is direction, so the window moves to the
 * label. Either way: one axis per tier, never both in one place. An earlier
 * draft had `In`/`Out`
 * here, which restated the group and then degraded to a bare `In` under a
 * heading reading `Inbound` on a mixed-window tenant: no information at all.
 *
 * The server owns the window (`throughput.windowHours`), so this reads it
 * rather than hardcoding "24h". When the cards disagree there is no single
 * window to name, so the label drops the number instead of picking one — a
 * header naming one window while the column held two would be a small
 * confident lie. Each row's flow cells then carry their own window in a
 * `title`, which is what keeps the figure disclosed when this label cannot
 * name it. That title is NOT optional for the same reason: an earlier draft
 * dropped it and this comment still claimed it existed.
 */
function flowHeader(mailboxes: MailboxHealthCard[]): string {
    const windows = new Set(mailboxes.map(mailbox => mailbox.throughput.windowHours));
    return windows.size === 1 ? `Flow ${[...windows][0]}h` : "Flow";
}

function MailboxList({ mailboxes }: iMailboxListProps) {
    // Not a table with zero rows. Every non-`ready` state of
    // useTenantInspection is indistinguishable from "no mailboxes
    // configured" if any of them ever renders as an empty table — the same
    // gap that made Generic/DataTable unusable for this feature (phase 1
    // spec §4).
    if (mailboxes.length === 0) {
        return (
            <div className="mp-empty">
                No mailboxes are configured for this tenant. That is a configuration statement rather than a fault —
                nothing here failed to load.
            </div>
        );
    }

    // Copied before sorting: `Array.prototype.sort` mutates, and this array
    // belongs to the hook's state.
    const ordered = mailboxes.slice().sort(compareMailboxes);

    return (
        <div className="mp-mb-scroll">
            <table className="mp-mb-table">
                <thead>
                    {/*
                        Two-tier grouped headers, the same idiom phase 1's
                        TenantTable uses — because that is the table the
                        operator was reading a moment before they clicked, so
                        its shape is the one already in their head.

                        The colSpans here sum to the row's cell count, and
                        MailboxRow's expander spans the same total. A test
                        asserts all three agree; nothing else would notice a
                        column added to one and missed in the others.
                    */}
                    <tr>
                        <th
                            rowSpan={2}
                            scope="col"
                        >
                            Mailbox
                        </th>
                        <th
                            colSpan={5}
                            scope="colgroup"
                            className="mp-group mp-boundary"
                        >
                            Inbound
                        </th>
                        <th
                            colSpan={4}
                            scope="colgroup"
                            className="mp-group mp-boundary"
                        >
                            Outbound
                        </th>
                        <th
                            rowSpan={2}
                            scope="col"
                            className="mp-boundary"
                        >
                            Last run
                        </th>
                    </tr>
                    <tr>
                        {/*
                            Backlog is under Inbound only, and that is correct
                            rather than a gap: backlog is mail waiting to be
                            collected, so it has no outbound meaning. It is why
                            the two groups differ by one column.
                        */}
                        <th
                            scope="col"
                            className="mp-r mp-boundary"
                        >
                            Backlog
                        </th>
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            Stalled
                        </th>
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            Failed
                        </th>
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            Given up
                        </th>
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            {flowHeader(mailboxes)}
                        </th>
                        <th
                            scope="col"
                            className="mp-r mp-boundary"
                        >
                            Stalled
                        </th>
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            Failed
                        </th>
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            Given up
                        </th>
                        <th
                            scope="col"
                            className="mp-r"
                        >
                            {flowHeader(mailboxes)}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {ordered.map(card => (
                        <MailboxRow
                            key={card.mailboxID}
                            card={card}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default MailboxList;
